import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import { useAuth } from '../context/AuthContext';
import {
  MENTORS, SERVICES, PLATFORM_STATS, TESTIMONIALS, FAQS,
  convertPrice, getAvatarUrl, SKILL_CHIPS
} from '../data/mockData';
import { getCached } from '../utils/dashboardCache';
import { EVENTS } from '../features/events/data';
import { useFeatureFlags } from '../context/FeatureFlagContext';
import { mentorService } from '../services';
import API_CONFIG from '../config/api.config';
import { useSEO, jsonLd } from '../utils/seo';
import './Home.css';

/* ── Sub-components ─────────────────────────── */

function RegBanner() {
  const { setModalOpen } = useContext(AppContext);
  const { user } = useAuth();
  if (user) return null; // already logged in — no need to push registration
  return (
    <div className="reg-banner">
      <div className="reg-banner-inner">
        <div className="rb-text">
          <div className="rb-title">🎓 New to DevBrains? Join FREE — No credit card needed.</div>
          <div className="rb-sub">Create your account in 30 seconds and book your first mentor today.</div>
        </div>
        <div className="rb-benefit"><span>✅</span> Free account</div>
        <div className="rb-benefit"><span>⚡</span> Book in 60 sec</div>
        <div className="rb-benefit"><span>🔒</span> Secure payments</div>
        <div className="rb-btns">
          <button className="btn btn-cta" onClick={() => setModalOpen(true)} style={{fontSize:'.84rem',padding:'9px 18px'}}>Create Free Account</button>
          <Link to="/mentors" className="btn btn-outline" style={{fontSize:'.84rem',padding:'9px 18px',borderColor:'rgba(255,255,255,.3)',color:'#fff'}}>Browse Mentors</Link>
        </div>
      </div>
    </div>
  );
}

function HeroSection() {
  const { setModalOpen, currency } = useContext(AppContext);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchVal, setSearchVal] = useState('');
  const [activeChip, setActiveChip] = useState('AI/ML');

  const HERO_PARAMS         = { page: 1, limit: 3, availability: 'today', sort: 'rating' };
  const HERO_PARAMS_FALLBACK = { page: 1, limit: 3, sort: 'rating' };
  const HERO_CACHE_KEY = `mentors:${JSON.stringify(HERO_PARAMS)}`;

  const [heroMentors, setHeroMentors] = useState(() => {
    if (API_CONFIG.MOCK) return MENTORS.filter(m => m.isAvailableToday).slice(0, 3);
    return getCached(HERO_CACHE_KEY)?.mentors || [];
  });
  const [heroLoading, setHeroLoading] = useState(
    !API_CONFIG.MOCK && !getCached(HERO_CACHE_KEY)
  );
  useEffect(() => {
    if (!API_CONFIG.MOCK) {
      mentorService.getMentors(HERO_PARAMS)
        .then(res => {
          const list = res.mentors || [];
          // If no mentors are available today, fall back to top-rated mentors
          if (list.length) return setHeroMentors(list);
          return mentorService.getMentors(HERO_PARAMS_FALLBACK)
            .then(fb => setHeroMentors(fb.mentors || []));
        })
        .catch(() => {})
        .finally(() => setHeroLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Role-aware hero CTA buttons
  const HeroCTAs = () => {
    if (!user) return (
      <>
        <Link to="/mentors" className="btn btn-cta btn-lg">🔍 Find My Mentor</Link>
        <button className="btn btn-outline btn-lg" onClick={() => setModalOpen(true)}>✨ Join Free Today</button>
        <Link to="/certifications" className="btn-link-ghost">🎓 Get Certified →</Link>
      </>
    );
    if (user.role === 'mentor') return (
      <>
        <Link to="/dashboard/mentor" className="btn btn-cta btn-lg">📊 My Dashboard</Link>
        <Link to="/mentors" className="btn btn-outline btn-lg">🔍 Browse Mentors</Link>
        <Link to="/dashboard/mentor/sessions" className="btn-link-ghost">📅 My Sessions →</Link>
      </>
    );
    // mentee or admin
    return (
      <>
        <Link to="/mentors" className="btn btn-cta btn-lg">🔍 Find My Mentor</Link>
        <Link to="/dashboard/mentee" className="btn btn-outline btn-lg">📊 My Dashboard</Link>
        <Link to="/certifications" className="btn-link-ghost">🎓 Get Certified →</Link>
      </>
    );
  };

  // Widget bottom CTA — no "Join Free" for logged-in users
  const WidgetCTA = () => {
    if (!user) return (
      <button className="btn btn-cta" style={{flex:1,whiteSpace:'nowrap'}} onClick={() => setModalOpen(true)}>Join Free</button>
    );
    if (user.role === 'mentor') return (
      <Link to="/dashboard/mentor/availability" className="btn btn-cta" style={{flex:1,whiteSpace:'nowrap',justifyContent:'center'}}>+ Add Slots</Link>
    );
    return (
      <Link to="/dashboard/mentee" className="btn btn-cta" style={{flex:1,whiteSpace:'nowrap',justifyContent:'center'}}>My Dashboard →</Link>
    );
  };

  return (
    <div className="hero-bg">
      <div className="hero-wrap">
        {/* Left column */}
        <div className="hero-left">
          <div className="hero-badge">
            <span className="badge-dot"></span>
            <span className="badge-label">2,400+ mentors · 50+ countries · India HQ</span>
          </div>
          <h1 className="hero-h1">
            Learn Tech from<br />
            <span className="accent2">Real Engineers.</span><br />
            <span className="accent">Book in 60 Seconds.</span>
          </h1>
          <p className="hero-sub">
            Stop wasting time on generic tutorials. Get 1:1 guidance from verified engineers worldwide,
            earn employer-trusted certificates, and fast-track your tech career globally.
          </p>
          <div className="hero-btns">
            <HeroCTAs />
          </div>
          <div className="hero-trust">
            <span className="trust-item">✅ Verified mentors</span>
            <span className="trust-sep"></span>
            <span className="trust-item">🔒 Escrow payments</span>
            <span className="trust-sep"></span>
            <span className="trust-item">⭐ 4.9/5 rating</span>
            <span className="trust-sep"></span>
            <span className="trust-item">🔄 Free rescheduling</span>
            <span className="trust-sep"></span>
            <span className="trust-item">🇮🇳 Founded in India</span>
          </div>
        </div>

        {/* Hero search widget */}
        <div className="hero-widget">
          <div className="hw-header">
            <div className="hw-title">🌍 Available Worldwide</div>
            <div className="hw-count">247 available now</div>
          </div>
          <div className="hw-body">
            <div className="hw-search">
              <input
                type="text"
                placeholder="Search skill or technology…"
                value={searchVal}
                onChange={e => setSearchVal(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && navigate('/mentors')}
              />
              <button onClick={() => navigate('/mentors')}>🔍 Search</button>
            </div>
            <div className="hw-chips">
              {SKILL_CHIPS.slice(0,6).map(c => (
                <button key={c.key} className={`chip ${activeChip === c.key ? 'active' : ''}`}
                  onClick={() => setActiveChip(c.key)}>
                  {c.label}
                </button>
              ))}
            </div>
            <div className="nearby-label">📍 Available today near you</div>
            <div className="nearby-list">
              {heroLoading ? [1,2,3].map(i => (
                <React.Fragment key={i}>
                  {i > 1 && <div className="nb-sep"></div>}
                  <div className="nearby-row" style={{pointerEvents:'none'}}>
                    <div className="nb-av-wrap">
                      <div className="nb-av" style={{background:'#e5e7eb'}}></div>
                    </div>
                    <div className="nb-info" style={{gap:6}}>
                      <div style={{height:13,background:'#e5e7eb',borderRadius:4,width:'55%'}}></div>
                      <div style={{height:11,background:'#e5e7eb',borderRadius:4,width:'80%'}}></div>
                      <div style={{height:10,background:'#e5e7eb',borderRadius:4,width:'40%'}}></div>
                    </div>
                    <div className="nb-right">
                      <div style={{height:13,background:'#e5e7eb',borderRadius:4,width:48,marginBottom:6}}></div>
                      <div style={{height:10,background:'#e5e7eb',borderRadius:4,width:36}}></div>
                    </div>
                  </div>
                </React.Fragment>
              )) : heroMentors.map((m, i) => (
                <React.Fragment key={m.id}>
                  {i > 0 && <div className="nb-sep"></div>}
                  <div className="nearby-row" onClick={() => navigate(`/mentors/${m.slug}`)}>
                    <div className="nb-av-wrap">
                      <img src={getAvatarUrl(m.avatarSeed, m.avatarBg, 42)} alt={m.name} className="nb-av-img"
                        onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                      <div className="nb-av" style={{background:`#${m.avatarBg}`,display:'none'}}>{m.initials}</div>
                    </div>
                    <div className="nb-info">
                      <div className="nb-name">{m.name}</div>
                      <div className="nb-tech">{m.skills.slice(0,3).join(' · ')} · {m.company}</div>
                      <div className="stars" style={{fontSize:'.72rem'}}>
                        {'★'.repeat(Math.round(m.rating))}
                        <span style={{color:'var(--ink4)',fontSize:'.65rem',fontWeight:500}}> {m.rating} ({m.reviewCount})</span>
                      </div>
                    </div>
                    <div className="nb-right">
                      <div className="nb-price">{convertPrice(m.priceINR, currency)}/hr</div>
                      <div className="nb-avail" style={{color: m.availabilityColor}}>{m.availabilityLabel}</div>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div style={{display:'flex',gap:8,marginTop:14}}>
              <Link to="/mentors" className="btn btn-primary" style={{flex:2,justifyContent:'center'}}>See all 2,400+ mentors →</Link>
              <WidgetCTA />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsStrip() {
  return (
    <div className="stats-strip">
      <div className="stats-inner">
        {PLATFORM_STATS.map(s => (
          <div key={s.label} className="stat-block">
            <div className="stat-num">{s.num}</div>
            <div className="stat-lbl">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LogoStrip() {
  const companies = ['Google','Microsoft','Grab','Shopee','TikTok','Axiata','GoTo','Petronas Digital','CIMB Tech','Maybank'];
  const doubled = [...companies, ...companies];
  return (
    <div className="logo-strip">
      <div className="logo-strip-inner">
        <div className="logo-label">Mentors from</div>
        <div className="logo-scroll-wrap">
          <div className="logo-scroll">
            {doubled.map((c, i) => <span key={i} className="logo-co">{c}</span>)}
          </div>
        </div>
      </div>
    </div>
  );
}

function ServicesSection() {
  const { setModalOpen, currency } = useContext(AppContext);
  const { user } = useAuth();
  const navigate = useNavigate();
  const handleServiceClick = () => user ? navigate('/mentors') : setModalOpen(true);
  return (
    <div className="services-section">
      <div className="section-wrap">
        <div style={{textAlign:'center'}}>
          <div className="section-label">Our Services</div>
          <h2 className="t-h2">Everything You Need to<br/>Level Up Your Tech Career</h2>
          <p className="t-body t-muted" style={{maxWidth:520,margin:'8px auto 0'}}>
            From your first session to your first job offer — DevBrains supports every step of your learning journey.
          </p>
        </div>
        <div className="services-grid">
          {SERVICES.map(s => (
            <div key={s.id} className={`svc-card ${s.color}`} onClick={handleServiceClick}>
              <div className="svc-ico">{s.icon}</div>
              <h4>{s.title}</h4>
              <p>{s.desc}</p>
              <div className="svc-price">From {convertPrice(s.priceINR, currency)}{s.priceUnit}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { icon: '🔍', title: 'Search Mentors', desc: 'Filter by skill, price range, rating, and live availability. See real slots — not waitlists.' },
    { icon: '📅', title: 'Pick a Slot', desc: 'Choose your session type and book a time that works for you — even today, even tonight.' },
    { icon: '💳', title: 'Pay Securely', desc: 'Razorpay escrow holds your money safely. It\'s only released after your session completes.' },
    { icon: '🚀', title: 'Learn & Earn', desc: 'Attend your session, complete projects, earn certificates, and land better opportunities.' },
  ];
  return (
    <div className="hiw-section">
      <div className="section-wrap">
        <div style={{textAlign:'center'}}>
          <div className="section-label">Simple Process</div>
          <h2 className="t-h2">Book Your First Session<br/>in Under 5 Minutes</h2>
          <p className="t-body t-muted" style={{maxWidth:420,margin:'8px auto 0'}}>No complicated setup. No long onboarding. Just find your mentor and go.</p>
        </div>
        <div className="hiw-steps">
          {steps.map((s, i) => (
            <div key={s.title} className="hiw-step">
              <div className="hiw-num">{i + 1}</div>
              <div className="hiw-ico">{s.icon}</div>
              <h4>{s.title}</h4>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TrustSection() {
  const trusts = [
    { icon: '✅', title: 'Every Mentor Verified', desc: 'Multi-step verification: LinkedIn check, credential review, test session, and background screening. Less than 12% of applicants are approved.' },
    { icon: '🔒', title: 'Escrow Payment Protection', desc: 'Your payment is held securely by Razorpay escrow — 256-bit encrypted. Funds only release after you confirm the session was completed.' },
    { icon: '🔄', title: 'Easy Refunds & Rescheduling', desc: 'Full refund if you cancel 24hrs before. Mentor no-shows get you a full refund + ₹400 credit. No questions asked.' },
    { icon: '🏆', title: 'Blockchain-Verified Certificates', desc: 'Every certificate issued on DevBrains is immutably recorded on blockchain and publicly verifiable. Employers can check authenticity instantly.' },
    { icon: '⭐', title: 'Real, Unfiltered Reviews', desc: 'We never delete or hide negative reviews. Every rating is from a real verified session — not paid promotions. What you see is what you get.' },
    { icon: '📊', title: 'Full Transparency Dashboard', desc: 'Track every rupee spent, every session booked, every certificate earned. Your data belongs to you — export it anytime.' },
  ];
  return (
    <div className="trust-section">
      <div className="section-wrap">
        <div style={{textAlign:'center'}}>
          <div className="trust-pill">🛡️ Why We're Trusted</div>
          <h2 className="t-h2" style={{color:'#f1f5f9',marginTop:8}}>
            Your Learning is 100% Protected.<br/>
            <span style={{color:'#4ade80'}}>Here's Exactly How.</span>
          </h2>
          <p style={{fontSize:'.9rem',color:'#94a3b8',maxWidth:480,margin:'10px auto 0',lineHeight:1.7}}>
            We built DevBrains with trust at the core — because your time, money, and career matter.
          </p>
        </div>
        <div className="trust-grid">
          {trusts.map(t => (
            <div key={t.title} className="trust-card">
              <span className="trust-card-ico">{t.icon}</span>
              <h4>{t.title}</h4>
              <p>{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TestimonialsSection() {
  return (
    <div className="testi-section">
      <div className="section-wrap">
        <div style={{textAlign:'center'}}>
          <div className="section-label">Success Stories</div>
          <h2 className="t-h2">Real Results from Real Learners</h2>
        </div>
        <div className="testi-grid">
          {TESTIMONIALS.map(t => (
            <div key={t.id} className={`testi ${t.featured ? 'featured' : ''}`}>
              <div className="stars" style={{marginBottom:10}}>{'★'.repeat(t.rating)}</div>
              <p className="testi-quote">"{t.quote}"</p>
              <div className="testi-author">
                <div className="t-av-wrap">
                  <img src={getAvatarUrl(t.avatarSeed, t.avatarBg, 38)} alt={t.name} className="t-av-img"
                    onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                  <div className="t-av" style={{background:`#${t.avatarBg}`,display:'none'}}>{t.initials}</div>
                </div>
                <div>
                  <div className="t-name">{t.name}</div>
                  <div className="t-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FaqSection() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="faq-section">
      <div className="section-wrap">
        <div style={{textAlign:'center',marginBottom:40}}>
          <div className="section-label">FAQ</div>
          <h2 className="t-h2">Questions? We've Got Answers.</h2>
        </div>
        <div className="faq-list" style={{maxWidth:760,margin:'0 auto'}}>
          {FAQS.map((f, i) => (
            <div key={i} className={`faq-item ${openIdx === i ? 'open' : ''}`}>
              <div className="faq-q" onClick={() => setOpenIdx(openIdx === i ? -1 : i)}>
                {f.q}
                <span className="faq-arrow">{openIdx === i ? '▲' : '▼'}</span>
              </div>
              {openIdx === i && <div className="faq-a">{f.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CtaSection() {
  const { setModalOpen } = useContext(AppContext);
  const { user } = useAuth();
  return (
    <div className="cta-section">
      <h2>Your Dream Tech Career<br/>Starts With One Session.</h2>
      <p>Join 18,000+ learners who stopped watching tutorials and started learning from real engineers. Your first session could change everything.</p>
      <div className="cta-btns">
        <Link to="/mentors" className="btn btn-white btn-lg">🔍 Find My Mentor</Link>
        {!user && (
          <button className="btn btn-cta btn-lg" onClick={() => setModalOpen(true)}>✨ Join Free — No Credit Card</button>
        )}
        {user && user.role === 'mentee' && (
          <Link to="/dashboard/mentee" className="btn btn-cta btn-lg">📊 Go to Dashboard</Link>
        )}
        {user && user.role === 'mentor' && (
          <Link to="/dashboard/mentor" className="btn btn-cta btn-lg">📊 Mentor Dashboard</Link>
        )}
        <Link to="/certifications" className="btn btn-white-ghost btn-lg">🎓 Get Certified</Link>
      </div>
      <div className="cta-proof">
        <div className="cta-proof-item">✅ Free to join</div>
        <div className="cta-proof-item">⚡ Book in 60 seconds</div>
        <div className="cta-proof-item">🔒 Escrow protected</div>
        <div className="cta-proof-item">🔄 Cancel anytime</div>
        <div className="cta-proof-item">⭐ 4.9/5 · 50+ countries</div>
      </div>
    </div>
  );
}

function Footer() {
  const { user } = useAuth();
  const showBecomeMentor = !user || user.role === 'mentee'; // hide for mentor & admin
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-brand">
            <div className="footer-logo">
              <div className="fl-mark">D</div>
              <div className="fl-name">DevBrains</div>
            </div>
            <p>Global tech mentorship platform. Book verified experts in 50+ countries, get certified, fast-track your career.</p>
            <div className="footer-company">JSDEVBRAINS Digital Private Limited<br/>Bengaluru, India 🇮🇳 · Serving 50+ Countries</div>
          </div>
          <div className="footer-col">
            <h5>For Learners</h5>
            <ul>
              <li><Link to="/blog/article/find-a-mentor-guide">Find a Mentor</Link></li>
              <li><Link to="/blog/article/get-certified-guide">Get Certified</Link></li>
              <li><Link to="/blog/article/browse-by-tech">Browse by Tech</Link></li>
              <li><Link to="/blog/article/group-sessions-guide">Group Sessions</Link></li>
              <li><Link to="/blog/article/career-roadmap-guide">Career Roadmap</Link></li>
            </ul>
          </div>
          {showBecomeMentor && (
            <div className="footer-col">
              <h5>For Mentors</h5>
              <ul>
                <li><Link to="/become-mentor">Apply Now</Link></li>
                <li><Link to="/blog/article/mentor-handbook">Mentor Handbook</Link></li>
                <li><Link to="/blog/article/mentor-payout-info">Payout Info</Link></li>
              </ul>
            </div>
          )}
          {user?.role === 'mentor' && (
            <div className="footer-col">
              <h5>Mentor Tools</h5>
              <ul>
                <li><Link to="/dashboard/mentor">My Dashboard</Link></li>
                <li><Link to="/dashboard/mentor/sessions">My Sessions</Link></li>
                <li><Link to="/dashboard/mentor/earnings">Earnings</Link></li>
                <li><Link to="/dashboard/mentor/availability">Availability</Link></li>
              </ul>
            </div>
          )}
          <div className="footer-col">
            <h5>Certifications</h5>
            <ul>
              <li><Link to="/certifications">AI/ML Track</Link></li>
              <li><Link to="/certifications">Full Stack Web</Link></li>
              <li><Link to="/certifications">Mobile Dev</Link></li>
              <li><Link to="/certifications">Cloud & DevOps</Link></li>
            </ul>
          </div>
          <div className="footer-col">
            <h5>Company</h5>
            <ul>
              <li><Link to="/blog">Help Centre</Link></li>
              <li><Link to="/news">Platform News</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2026 JSDEVBRAINS Digital Private Limited, India. All rights reserved.</span>
          <div className="fb-right">
            <span>🔒 Razorpay Secured</span>
            <span>🔗 Blockchain Certs</span>
            <span>🇮🇳 Made in India</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ── Mobile App Download Section ────────────────── */
function MobileAppSection() {
  return (
    <section className="mobile-app-section">
      <div className="mobile-app-inner">

        {/* Left — phones mockup */}
        <div className="ma-visual">
          <div className="ma-phone ma-phone-back">
            <div className="ma-screen">
              <div className="ma-screen-header">
                <div className="ma-screen-dot" /><div className="ma-screen-dot" /><div className="ma-screen-dot" />
              </div>
              <div className="ma-screen-card">
                <div className="ma-sc-av">AK</div>
                <div>
                  <div className="ma-sc-name">Amar Krishnan</div>
                  <div className="ma-sc-role">Ex-Google · React</div>
                </div>
                <div className="ma-sc-rating">⭐ 4.97</div>
              </div>
              <div className="ma-screen-card">
                <div className="ma-sc-av ma-av2">SN</div>
                <div>
                  <div className="ma-sc-name">Siti Nabilah</div>
                  <div className="ma-sc-role">Shopee · AI/ML</div>
                </div>
                <div className="ma-sc-rating">⭐ 5.0</div>
              </div>
              <div className="ma-screen-label">📅 Session in 10 min</div>
              <div className="ma-screen-btn">Join Now →</div>
            </div>
          </div>
          <div className="ma-phone ma-phone-front">
            <div className="ma-screen ma-screen-dark">
              <div className="ma-notch" />
              <div className="ma-dash-row">
                <span>👋 Hey Suthahar</span>
                <div className="ma-av-tiny">S</div>
              </div>
              <div className="ma-stat-row">
                <div className="ma-stat"><div className="ma-stat-n">4</div><div className="ma-stat-l">Sessions</div></div>
                <div className="ma-stat"><div className="ma-stat-n">2</div><div className="ma-stat-l">Reviews</div></div>
                <div className="ma-stat"><div className="ma-stat-n">1</div><div className="ma-stat-l">Cert</div></div>
              </div>
              <div className="ma-upcoming">
                <div className="ma-up-label">Upcoming</div>
                <div className="ma-up-card">
                  <div className="ma-up-dot" />
                  <div>
                    <div className="ma-up-title">Code Review</div>
                    <div className="ma-up-time">Today · 3:00 PM</div>
                  </div>
                  <div className="ma-up-join">Join</div>
                </div>
              </div>
              <div className="ma-mentors-label">Top Mentors</div>
              <div className="ma-mentor-chips">
                {['AK','SN','RM','PP'].map((i,idx)=>(
                  <div key={idx} className={`ma-chip ma-chip-${idx}`}>{i}</div>
                ))}
                <div className="ma-chip-more">+240</div>
              </div>
            </div>
          </div>
          {/* Floating badges */}
          <div className="ma-badge ma-badge-1">🎉 Session booked!</div>
          <div className="ma-badge ma-badge-2">⭐ 4.97 rating</div>
          <div className="ma-badge ma-badge-3">🏆 Cert earned</div>
        </div>

        {/* Right — copy + download buttons */}
        <div className="ma-content">
          <div className="ma-pill">📱 Now on Mobile</div>
          <h2 className="ma-heading">
            Your mentor is<br />
            <span>one tap away</span>
          </h2>
          <p className="ma-sub">
            Book sessions, join meetings, track your progress, and earn
            certificates — all from your phone. The DevBrains app is
            built for engineers on the go.
          </p>

          <ul className="ma-features">
            <li>📅 Book & manage sessions from your phone</li>
            <li>🔔 Real-time notifications before your session</li>
            <li>📹 Join Google Meet & Zoom in one tap</li>
            <li>🏆 Track certification progress on the go</li>
            <li>💬 Message your mentor between sessions</li>
          </ul>

          <div className="ma-store-buttons">
            <a
              href="https://apps.apple.com/app/devbrains"
              target="_blank"
              rel="noreferrer"
              className="ma-store-btn ma-apple"
              onClick={e => { e.preventDefault(); alert('Coming soon to App Store! 🍎'); }}
            >
              <div className="ma-store-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div className="ma-store-text">
                <div className="ma-store-sub">Download on the</div>
                <div className="ma-store-name">App Store</div>
              </div>
            </a>

            <a
              href="https://play.google.com/store/apps/details?id=in.devbrains"
              target="_blank"
              rel="noreferrer"
              className="ma-store-btn ma-google"
              onClick={e => { e.preventDefault(); alert('Coming soon to Google Play! 🤖'); }}
            >
              <div className="ma-store-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
                  <path d="M3.18 23.76c.35.2.76.22 1.14.08l12.29-7.08-2.59-2.59-10.84 9.59zm-1.76-20.5C1.16 3.57 1 3.89 1 4.28v15.44c0 .39.16.71.42.99l.08.07 8.65-8.65v-.2L1.42 3.26zm18.7 8.28-2.44-1.41-2.89 2.89 2.89 2.89 2.46-1.42c.7-.4.7-1.55-.02-1.95zm-17 11.44 10.83-9.6-2.59-2.59L3.1 21.87l.02.11z"/>
                </svg>
              </div>
              <div className="ma-store-text">
                <div className="ma-store-sub">Get it on</div>
                <div className="ma-store-name">Google Play</div>
              </div>
            </a>
          </div>

          <div className="ma-ratings-row">
            <div className="ma-rating-item">
              <div className="ma-rating-stars">★★★★★</div>
              <div className="ma-rating-text">4.9 on App Store</div>
            </div>
            <div className="ma-rating-sep" />
            <div className="ma-rating-item">
              <div className="ma-rating-stars">★★★★★</div>
              <div className="ma-rating-text">4.8 on Play Store</div>
            </div>
            <div className="ma-rating-sep" />
            <div className="ma-rating-item">
              <div className="ma-rating-stars">📥</div>
              <div className="ma-rating-text">10k+ Downloads</div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
function UpcomingEventsSection() {
  const upcoming = EVENTS.filter(e => e.status === 'upcoming').slice(0, 3);
  if (!upcoming.length) return null;

  return (
    <section className="events-section">
      <div className="events-section-inner">
        <div className="section-head">
          <div className="sh-badge">📅 Live Events</div>
          <h2>Upcoming Mentor <span>Events & Workshops</span></h2>
          <p>Free and paid live sessions from top mentors. Learn, ask questions, and connect.</p>
        </div>
        <div className="ues-grid">
          {upcoming.map(e => (
            <div key={e.id} className="ues-card">
              <div className="ues-type-row">
                <span className="ues-type">{e.type.replace('_', ' ')}</span>
                {e.priceINR === 0
                  ? <span className="ues-free">FREE</span>
                  : <span className="ues-price">₹{e.priceINR}</span>
                }
              </div>
              <h4 className="ues-title">{e.title}</h4>
              <div className="ues-mentor-row">
                <img src={getAvatarUrl(e.mentorAvatar, e.mentorBg, 28)}
                  alt={e.mentor} className="ues-av"
                  onError={ev => { ev.target.style.display='none'; ev.target.nextSibling.style.display='flex'; }} />
                <div className="ues-av-fb" style={{ background: `#${e.mentorBg}` }}>{e.mentor[0]}</div>
                <span className="ues-mentor">{e.mentor}</span>
              </div>
              <div className="ues-meta">
                <span>📅 {e.date}</span>
                <span>🕐 {e.time}</span>
                <span>👥 {e.registeredCount}/{e.maxAttendees}</span>
              </div>
              <div className="ues-tags">
                {e.tags.map(t => <span key={t} className="ues-tag">{t}</span>)}
              </div>
              <Link to="/events" className="ues-btn">
                {e.priceINR === 0 ? 'Register Free →' : `Register →`}
              </Link>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <Link to="/events" className="btn btn-outline">View All Events →</Link>
        </div>
      </div>
    </section>
  );
}

/* ── Main Home Page ─────────────────────────── */
export default function Home() {
  const { isEnabled } = useFeatureFlags();

  useSEO({
    title: 'DevBrains — 1:1 Mentorship from Top Tech Engineers',
    description: 'Connect with verified engineers from Google, Shopee, Grab for mentorship, code reviews, mock interviews, and certifications.',
    jsonLd: jsonLd.organization(),
  });

  return (
    <div style={{paddingTop: 64}}>
      <RegBanner />
      <HeroSection />
      <StatsStrip />
      <LogoStrip />
      <ServicesSection />
      <HowItWorks />
      {isEnabled('mentorEvents') && <UpcomingEventsSection />}
      {isEnabled('mobileRelease') && <MobileAppSection />}
      <TrustSection />
      <TestimonialsSection />
      <FaqSection />
      <CtaSection />
      <Footer />
    </div>
  );
}
